import { Layer, Layers, MapObjectSpawnMode, ZIndexes } from "../constants";
import { CircleHitbox, GroupHitbox, type Hitbox, RectangleHitbox } from "../utils/hitbox";
import { DefinitionType } from "../utils/objectDefinitions";
import { FloorNames } from "../utils/terrain";
import { Vec, type Vector } from "../utils/vector";
import "../utils/isClient";
import { type BuildingDefinition, type BuildingObstacle } from "./buildings";
import { POLICE_CELL_BARS } from "./fargoObstacles";
import {
    LIGHTHOUSE_DECK, LIGHTHOUSE_ROOF_PIECES, MANSION_LANTERN_GLASS, POLICE_DECK, POLICE_GLASS, POLICE_ROOF_PIECES,
    POOL_HALL_GLASS, POOL_LOBBY_DECK, POOL_LOBBY_ROOF_PIECES, POOL_SHOWERS_DECK, POOL_SHOWERS_GLASS, POOL_SHOWERS_ROOF_PIECES
} from "./roofs";
import POLICE_STATION_PLAN from "./plans/police_station_hall.json";
import PRISON_LIGHTHOUSE_PLAN from "./plans/prison_lighthouse.json";
import POOL_PLAN from "./plans/fargo_pool.json";
import POOL_BLUE_PLAN from "./plans/fargo_pool_blue.json";
import POOL_RED_PLAN from "./plans/fargo_pool_red.json";
import POOL_SHOWERS_PLAN from "./plans/fargo_pool_showers.json";
import POOL_LOBBY_PLAN from "./plans/fargo_pool_lobby.json";
import MANSION_LOT_PLAN from "./plans/fargo_mansion_lot.json";
import MANSION_A_HOUSE_PLAN from "./plans/fargo_mansion_a_house.json";
import MANSION_A_UPPER_PLAN from "./plans/fargo_mansion_a_upper.json";
import MANSION_B_HOUSE_PLAN from "./plans/fargo_mansion_b_house.json";
import MANSION_B_UPPER_PLAN from "./plans/fargo_mansion_b_upper.json";
import MANSION_GARAGE_PLAN from "./plans/fargo_mansion_garage.json";

declare const IS_CLIENT: boolean;

/*
    Fargo's buildings of 1.07 (the owner's picture, 29 October 2026, of the map grown north: "police station", the
    "Prison lighthouse ... like lighthouse, but different, observing prison", "remove the 4 houses, replace with Two, new,
    very detailed, different looking from the inside (similar outside aura), mansions, with two floors, fence walls,
    parking etc." and "replace lighthouse with csgo pool day type location please,"), and Pool Day of 1.07.1 in the pool
    villa's place (the owner, the same day: "regarding the pool, i want it to be like pool day map in csgo, a little
    different of course, but mainly like that pleasE..."). Each building's walls, doors, windows and rooms are its plan's
    (common/src/definitions/plans/<name>.json, written by its generator in tools/fargo/, which draws its pictures from
    the same plan: tests/src/fargo107.test.ts checks the two agree); what stands in it is placed here, in its own units
    (its middle at 0, 0, x east, y south).
*/

/** a building's plan as its generator writes it (tools/fargo/plans.py) */
interface BuildingPlan {
    readonly name: string
    readonly half: readonly number[]
    /** x0, y0, x1, y1 and what the wall is ("outer", "inner", "glass") */
    readonly walls: ReadonlyArray<ReadonlyArray<number | string>>
    /** what's in each gap left in a wall: a door, a pair of doors, a window... */
    readonly openings: ReadonlyArray<{
        readonly kind: string
        readonly axis: string
        readonly at: number
        readonly from: number
        readonly to: number
        readonly t: number
    }>
    /** each room to its walls' middles: x0, y0, x1, y1 and its floor (FloorNames) */
    readonly rooms: Readonly<Record<string, ReadonlyArray<number | string>>>
    readonly extra?: Readonly<Record<string, unknown>>
}

type PlanOpening = BuildingPlan["openings"][number];

const planBox = (x0: number, y0: number, x1: number, y1: number): RectangleHitbox =>
    new RectangleHitbox(Vec(Math.min(x0, x1), Math.min(y0, y1)), Vec(Math.max(x0, x1), Math.max(y0, y1)));

/** a plan's walls (all of them, or only those of the kinds given) as one hitbox */
const planWalls = (plan: BuildingPlan, kinds?: readonly string[]): GroupHitbox => new GroupHitbox(
    ...plan.walls
        .filter(wall => kinds === undefined || kinds.includes(wall[4] as string))
        .map(([x0, y0, x1, y1]) => planBox(x0 as number, y0 as number, x1 as number, y1 as number))
);

/** a plan's rooms' floors */
const planFloors = (plan: BuildingPlan, layer?: number): NonNullable<BuildingDefinition["floors"]> => Object.values(plan.rooms).map(([x0, y0, x1, y1, floor]) => ({
    type: floor as FloorNames,
    hitbox: planBox(x0 as number, y0 as number, x1 as number, y1 as number),
    ...(layer === undefined ? {} : { layer })
}));

/** the middle of a gap in a wall */
const gapMiddle = ({ axis, at, from, to }: PlanOpening): Vector => (axis === "x" ? Vec((from + to) / 2, at) : Vec(at, (from + to) / 2));

/**
 * a door filling a gap: its hitbox sits `offset` off its middle towards its latch, which turns with it (east at rotation
 * 0, north at 1), so it's placed that far the other way. In a wall along x it's turned 0 (its hinge west) or 2, along y 1
 * (its hinge south) or 3
 */
const gapDoor = (idString: string, opening: PlanOpening, offset: number, flip = false): BuildingObstacle => {
    const rotation = (opening.axis === "x" ? 0 : 1) + (flip ? 2 : 0);
    const along = (opening.from + opening.to) / 2 + (rotation === 0 || rotation === 3 ? offset : -offset);
    return { idString, position: opening.axis === "x" ? Vec(along, opening.at) : Vec(opening.at, along), rotation };
};

/** a pair of doors filling a gap, each hinged at its end of it */
const gapDoors = (idString: string, opening: PlanOpening, width: number): BuildingObstacle[] => {
    const middle = gapMiddle(opening);
    const half = width / 2;
    return opening.axis === "x"
        ? [
            { idString, position: Vec(middle.x - half, middle.y), rotation: 0 },
            { idString, position: Vec(middle.x + half, middle.y), rotation: 2 }
        ]
        : [
            { idString, position: Vec(middle.x, middle.y + half), rotation: 1 },
            { idString, position: Vec(middle.x, middle.y - half), rotation: 3 }
        ];
};

/** a door's way of opening: the gap's middle (x, y) and the point it always swings away from */
type Swing = readonly [number, number, number, number];

/**
 * what usually stands in a plan's gaps: its doors and windows (`flipped` doors hinge at their other end; a door in
 * `swings` always opens away from its point; an upper floor's windows don't break, `upper`)
 */
const planOpenings = (
    plan: BuildingPlan,
    flipped: ReadonlyArray<readonly [number, number]> = [],
    swings: readonly Swing[] = [],
    upper = false
): BuildingObstacle[] => plan.openings.flatMap(opening => {
    const middle = gapMiddle(opening);
    const near = (x: number, y: number): boolean => Math.abs(middle.x - x) < 0.01 && Math.abs(middle.y - y) < 0.01;
    const flip = flipped.some(([x, y]) => near(x, y));
    const swing = swings.find(([x, y]) => near(x, y));
    const swingsFrom = swing === undefined ? {} : { swingsFrom: Vec(swing[2], swing[3]) };
    switch (opening.kind) {
        case "door": return [{ ...gapDoor("door", opening, 0.44, flip), ...swingsFrom }];
        // a pair of doors, each hinged at its end of the gap, opening from the middle
        case "door_double": {
            const half = (opening.to - opening.from) / 2;
            const first = { ...opening, to: opening.from + half };
            const second = { ...opening, from: opening.from + half };
            return [
                { ...gapDoor("door", first, 0.44, opening.axis === "y"), ...swingsFrom },
                { ...gapDoor("door", second, 0.44, opening.axis === "x"), ...swingsFrom }
            ];
        }
        // a garage's door, shut (it breaks), and a bay left open
        case "garage": return [{ idString: "mansion_garage_door", position: middle, rotation: opening.axis === "x" ? 0 : 1 }];
        case "garage_open": return [];
        // a cubicle's door (Pool Day's toilets), hinged at the gap's west end (north, in a wall along y)
        case "stall_blue": return [gapDoor("pool_stall_door_blue", opening, 0, flip)];
        case "stall_red": return [gapDoor("pool_stall_door_red", opening, 0, flip)];
        case "metal": return [gapDoor("metal_door", opening, 0.25, flip)];
        case "glass_double": return gapDoors("glass_door", opening, 10.86);
        case "gate": return gapDoors("iron_gate", opening, 10.2);
        case "metal_double": return gapDoors("metal_door", opening, 10.5).map(door => ({
            ...door,
            // (a metal door's hitbox sits 0.25 off its middle: each of the pair moved that much towards its latch's end)
            position: opening.axis === "x"
                ? Vec(door.position.x + (door.rotation === 0 ? 0.25 : -0.25), door.position.y)
                : Vec(door.position.x, door.position.y + (door.rotation === 1 ? -0.25 : 0.25))
        }));
        case "window":
        case "mirror":
        case "counter":
            return [{ idString: upper ? "apt_window" : "window", position: gapMiddle(opening), rotation: opening.axis === "x" ? 1 : 0 }];
        default: return [];
    }
});

// ============================== the police station (tools/fargo/police.py) ==============================
const POLICE_PLAN = POLICE_STATION_PLAN as unknown as BuildingPlan;
/** the station's place in its compound, and the sally port garage's */
const POLICE_STATION_AT = Vec(-26, 0);
const POLICE_GARAGE_AT = Vec(125, -5.5);

/** a furniture piece (x, y in the station's units; rotation: the wall its back is to, 0 north, 1 west, 2 south, 3 east) */
const at = (idString: BuildingObstacle["idString"], x: number, y: number, rotation = 0, extra: Partial<BuildingObstacle> = {}): BuildingObstacle =>
    ({ idString, position: Vec(x, y), rotation, ...extra });

/**
 * four desks back to back round (x, y), their chairs out either side: the bullpen's pods. The north pair's users sit
 * north of them, facing south (their desks' backs south), the south pair's south of them
 */
const deskPod = (x: number, y: number): BuildingObstacle[] => [
    at("police_desk", x - 6, y - 3, 2), at("police_desk", x + 6, y - 3, 2),
    at("police_desk", x - 6, y + 3, 0), at("police_desk", x + 6, y + 3, 0),
    at("office_chair", x - 6, y - 9, 0), at("office_chair", x + 6, y - 9, 0),
    at("office_chair", x - 6, y + 9, 2), at("office_chair", x + 6, y + 9, 2)
];

/** a cell's front: bars, its sliding door, bars; its bunk and its toilet (x0, x1: its inside) */
const policeCell = ([x0, x1]: readonly number[]): BuildingObstacle[] => {
    const front = POLICE_PLAN.extra?.cellFront as number;
    const middle = (x0 + x1) / 2;
    return [
        at(`prison_bars_${POLICE_CELL_BARS}`, x0 + POLICE_CELL_BARS / 2, front),
        at("prison_cell_door", middle, front),
        at(`prison_bars_${POLICE_CELL_BARS}`, x1 - POLICE_CELL_BARS / 2, front),
        at("prison_bed", x0 + 4.1, -96),
        at("prison_toilet", x1 - 3, -106)
    ];
};

/** the station's furniture, room by room */
const POLICE_FURNITURE: readonly BuildingObstacle[] = [
    // ---- the locker room: the staff's lockers along its east wall, benches, a basin; the showers along the west wall
    at("prison_lockers", -78.8, -98, 3), at("prison_lockers", -78.8, -84, 3), at("prison_lockers", -78.8, -70, 3),
    at("prison_bench", -94, -92, 1), at("prison_bench", -94, -72, 1),
    at("sink2", -116, -55.9, 2),
    at("trash_can", -84, -56.5),
    // ---- the briefing room: the screen on the west wall over the speaker's platform, the lectern, six rows of seats
    // facing it, plants, water
    at("police_screen", -122.6, -4, 1),
    at("police_lectern", -117, -4, 1),
    ...[-104, -95, -86].flatMap(x => [at("police_seats", x, -27, 3), at("police_seats", x, 18, 3)]),
    at("potted_plant", -81, -47), at("potted_plant", -81, 39),
    at("water_cooler", -120, 39, 2),
    // ---- the break room: the coffee, the sink and the fridge along its north wall, two vending machines, two tables
    at("police_coffee_counter", -115, 47.3, 0),
    at("apt_kitchen_sink", -104.5, 47.9, 0),
    at("fridge", -83, 48.2, 0),
    at("vending_machine", -80.2, 66, 3), at("vending_machine", -80.2, 77, 3),
    at("round_table", -106, 72),
    at("chair", -106, 63.6, 0), at("chair", -106, 80.4, 2), at("chair", -114.4, 72, 1),
    at("round_table", -92, 96),
    at("chair", -92, 87.6, 0), at("chair", -100.4, 96, 1), at("chair", -83.6, 96, 3),
    at("trash_can", -80.5, 105.5),
    // ---- the captain's office: the desk facing the door, the chair behind it and two before it, the bookcases, the
    // filing cabinets, the gun case
    at("executive_desk", -59, -84, 2),
    at("office_chair", -59, -91.5, 0),
    at("chair", -64.5, -74.5, 2), at("chair", -53.5, -74.5, 2),
    at("bookshelf", -72.9, -98, 1), at("bookshelf", -72.9, -84, 1),
    at("filing_cabinet", -46, -104, 3), at("filing_cabinet", -46, -97, 3),
    at("gun_case", -45.4, -72, 3),
    at("potted_plant", -71, -57.5),
    // ---- the detectives' room: three desks, the case table, the files
    at("police_desk", -33, -105.5, 0), at("police_desk", -15, -105.5, 0),
    at("office_chair", -33, -99.2, 2), at("office_chair", -15, -99.2, 2),
    at("police_desk", -15, -73, 0), at("office_chair", -15, -66.7, 2),
    at("police_case_table", -30.5, -80, 1),
    at("filing_cabinet", -10, -88, 3), at("filing_cabinet", -10, -81, 3),
    // ---- the interview rooms: a table bolted down, a chair either side; between them the observation room's desk
    at("police_interview_table", 6, -86), at("chair", 6, -93.6, 0), at("chair", 6, -78.4, 2),
    at("police_interview_table", 44, -86), at("chair", 44, -93.6, 0), at("chair", 44, -78.4, 2),
    at("police_monitor_desk", 25, -106.8, 0), at("office_chair", 25, -100.8, 2),
    // ---- the bullpen: six pods of four desks, the front desks behind the lobby's glass, the copier, the files
    ...deskPod(-50, -28), ...deskPod(-6, -28), ...deskPod(34, -28),
    ...deskPod(-50, 12), ...deskPod(-6, 12), ...deskPod(34, 12),
    at("police_front_desk", -47, 40.5, 2), at("office_chair", -47, 35, 0),
    at("police_front_desk", -7, 40.5, 2), at("office_chair", -7, 35, 0),
    at("police_copier", 50.5, -47.3, 0),
    at("filing_cabinet", -72, -5, 1), at("filing_cabinet", -72, 2, 1),
    at("water_cooler", 51.5, 38.5, 3),
    at("potted_plant", -71, -47), at("potted_plant", -71, 39.5),
    at("trash_can", 20, 39.5), at("trash_can", -27, -47.5),
    // ---- the lobby: benches along its west wall, the metal detector inside the doors, plants, a bin
    at("museum_bench", -73.5, 62, 1), at("museum_bench", -73.5, 76, 1), at("museum_bench", -73.5, 90, 1),
    at("police_metal_detector", -33.1, 99.5),
    at("potted_plant", -71, 105.5), at("potted_plant", 3, 49.5),
    at("trash_can", -15, 105.5),
    // ---- the restrooms
    at("toilet", 28.5, 50.5, 3), at("toilet", 28.5, 60, 3), at("sink2", 15, 74.1, 2),
    at("toilet", 28.5, 84, 3), at("toilet", 28.5, 93.5, 3), at("sink2", 15, 81.9, 0),
    // ---- the records room: filing cabinets down its east wall, evidence shelves down its west wall and along the south
    ...[56, 63, 70, 77, 84].map(y => at("filing_cabinet", 52, y, 3)),
    at("police_evidence_shelf", 35.2, 70, 1), at("police_evidence_shelf", 35.2, 86, 1),
    at("police_evidence_shelf", 45, 106.8, 2),
    // ---- the cells: three, each its bars and sliding door, a bunk and a toilet; benches along the corridor
    ...(POLICE_PLAN.extra?.cells as ReadonlyArray<readonly number[]>).flatMap(policeCell),
    at("prison_bench", 72, -55.4, 2), at("prison_bench", 90, -55.4, 2),
    // ---- booking: the counter the arrested come to from the sally port, a desk, the fingerprint scanner, the
    // property lockers, the bench to wait on (the camera's spot is painted on the floor by its north wall)
    at("police_booking_counter", 92, -10, 3), at("office_chair", 86, -10, 1),
    at("police_desk", 68, -48, 0), at("office_chair", 68, -41.7, 2),
    at("police_fingerprint", 104, -1.7, 0),
    at("prison_lockers", 58.8, -5, 1),
    at("prison_bench", 102, 41.6, 2),
    at("trash_can", 60, 40),
    // ---- the armory: gun lockers along its south and east walls, ammo, a gun case on the bench
    ...[66, 81, 96, 111].map(x => at("gun_locker", x, 106.9, 2)),
    at("gun_locker", 120.9, 62, 3), at("gun_locker", 120.9, 78, 3),
    at("ammo_crate", 62, 62), at("ammo_crate", 62, 72),
    at("gun_case", 95, 75, 0)
];

/** the compound's ground: the parking lots' asphalt, the sidewalks' and the plaza's concrete, the grass between */
const POLICE_ASPHALT = new GroupHitbox(
    planBox(-303, -164, -158, 164),         // the public lot
    planBox(-152, -164, 100, -118),         // the staff lot north of the station
    planBox(100, -164, 303, 164)            // the motor pool
);
const POLICE_CONCRETE = new GroupHitbox(
    planBox(-158, -164, -152, 164),         // the sidewalk along the station's west side
    planBox(-152, -118, 100, -112),         // and its north side
    planBox(-152, 112, 100, 164)            // the plaza before its front doors
);
/** the public lot's bays: their painted lines (two double rows, six bays a row), and the staff lot's */
const policeBayLines = (): RectangleHitbox[] => {
    const lines: RectangleHitbox[] = [];
    for (const y of [-60, 60]) {
        lines.push(planBox(-297, y - 0.3, -197, y + 0.3));
        for (let i = 0; i <= 6; i++) {
            const x = -297 + i * (100 / 6);
            lines.push(planBox(x - 0.3, y - 34, x + 0.3, y + 34));
        }
    }
    for (let i = 0; i <= 12; i++) {
        const x = -148 + i * 20;
        lines.push(planBox(x - 0.3, -152, x + 0.3, -120));
    }
    // the motor pool's: along its north and south fences
    for (const y0 of [-156, 124]) {
        for (let i = 0; i <= 6; i++) {
            const x = 166 + i * 20;
            lines.push(planBox(x - 0.3, y0, x + 0.3, y0 + 32));
        }
    }
    return lines;
};

/** the motor pool's fence (breakable, as the prison's), a gap left for its gate onto the road */
const policeFence = (from: Vector, to: Vector): BuildingObstacle[] => {
    const length = Vec.len(Vec.sub(to, from));
    const count = Math.max(1, Math.ceil(length / 8.3));
    const rotation = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y) ? 0 : 1;
    return Array.from({ length: count }, (_, i) => ({ idString: "fence", position: Vec.lerp(from, to, (i + 0.5) / count), rotation }));
};

/** a car at (x, y) facing `rotation` (0 east, 1 north, 2 west, 3 south) */
const carAt = (idString: string, x: number, y: number, rotation: number): BuildingObstacle => ({ idString, position: Vec(x, y), rotation });

// ============================== the prison's lighthouse (tools/fargo/lighthouse.py) ==============================
const LIGHTHOUSE_PLAN = PRISON_LIGHTHOUSE_PLAN as unknown as BuildingPlan;
const LIGHTHOUSE_TOWER = LIGHTHOUSE_PLAN.extra?.tower as { readonly center: readonly number[], readonly radius: number, readonly wall: number };
const LIGHTHOUSE_AT = Vec(LIGHTHOUSE_TOWER.center[0], LIGHTHOUSE_TOWER.center[1]);
/** the tower's round wall: a ring of circles, its door's gap left (the plan's) */
const LIGHTHOUSE_RING = (LIGHTHOUSE_PLAN.extra?.ring as ReadonlyArray<readonly number[]>).map(([x, y, r]) => new CircleHitbox(r, Vec(x, y)));
const planRoom = (plan: BuildingPlan, name: string): RectangleHitbox => {
    const [x0, y0, x1, y1] = plan.rooms[name] as readonly number[];
    return planBox(x0, y0, x1, y1);
};

// ============================== Pool Day, the public pool (tools/fargo/pool.py) ==============================
const POOL_COMPOUND = POOL_PLAN as unknown as BuildingPlan;
const POOL_BLUE = POOL_BLUE_PLAN as unknown as BuildingPlan;
const POOL_RED = POOL_RED_PLAN as unknown as BuildingPlan;
const POOL_SHOWERS = POOL_SHOWERS_PLAN as unknown as BuildingPlan;
const POOL_LOBBY = POOL_LOBBY_PLAN as unknown as BuildingPlan;
const POOL_WATER = POOL_COMPOUND.extra?.pool as readonly number[];
const POOL_TUB = POOL_COMPOUND.extra?.tub as readonly number[];
/** where each of the compound's four buildings stands in it */
const poolPart = (key: string): Vector => {
    const [x, y] = POOL_COMPOUND.extra?.[key] as readonly number[];
    return Vec(x, y);
};
const POOL_BLUE_STALLS = POOL_BLUE.extra?.stalls as readonly number[];
const POOL_RED_STALLS = POOL_RED.extra?.stalls as readonly number[];
const POOL_ISLAND = POOL_RED.extra?.island as readonly number[];
/** a plan's half size as its spawn hitbox (its walls' outer faces) and its ceiling (to their middles, `inset` in) */
const planSpawn = (plan: BuildingPlan): RectangleHitbox => RectangleHitbox.fromRect(plan.half[0] * 2, plan.half[1] * 2);
const planCeiling = (plan: BuildingPlan, inset: number): RectangleHitbox => RectangleHitbox.fromRect(plan.half[0] * 2 - 2 * inset, plan.half[1] * 2 - 2 * inset);

/**
 * the sunbeds along one side of the pool (`y` their middles' line; their heads to the halls: north side 0, south side 2),
 * a player's way left between each two and between the first and the shower side's arches; the north side's leave the
 * lifeguard's chair its place
 */
const poolSunbeds = (y: number, rotation: number): BuildingObstacle[] =>
    [-82, -72.5, -63, -53.5, -44, -34.5, -25, -15.5, 4, 13.5, 23, 32.5, 42, 51.5].map(x => at("pool_lounger", x, y, rotation));
/**
 * a hall's six banks of lockers: four along its back wall between its glass doors (their backs to it, along `y`: the blue
 * hall's north, 0, the red's south, 2) and one against each end wall, by the window (`endsY` their middles; not under
 * the windows, where 1.07.1's lab found them)
 */
const poolLockers = (idString: "pool_lockers_blue" | "pool_lockers_red", y: number, rotation: number, endsY: number): BuildingObstacle[] => [
    ...[-21, -7, 7, 21].map(x => at(idString, x, y, rotation)),
    at(idString, -72.5, endsY, 1), at(idString, 72.5, endsY, 3)
];

// ============================== the mansions (tools/fargo/mansions.py, tools/fargo/furniture.py) ==============================
/*
    Each mansion (1.07; the owner's picture: "remove the 4 houses, replace with Two, new, very detailed, different looking
    from the inside (similar outside aura), mansions, with two floors, fence walls, parking etc.") is its grounds
    (`fargo_mansion_a`, `fargo_mansion_b`: the fence, the gates, the drive, the fountain, the cars, the garden), its house
    and its upper floor (`fargo_mansion_a_house` and `fargo_mansion_a_upper`, B's), the portico and the garage. The two
    are alike outside and nothing alike inside. A house's two floors are two buildings at one place, as the apartment
    blocks' are: the flights go up the hall from the ground floor (their layer) to the gallery upstairs; the closet under
    the gallery past a flight's top is solid downstairs only; the floor upstairs is open over the hall (`void`, both
    floors' `floorOpenings`), a railing along the gallery's edge. Everything is in the house's units (its middle at 0, 0,
    x east, y south), the grounds' in theirs.
*/
const MANSION_LOT = MANSION_LOT_PLAN as unknown as BuildingPlan;
const MANSION_A_HOUSE = MANSION_A_HOUSE_PLAN as unknown as BuildingPlan;
const MANSION_A_UPPER = MANSION_A_UPPER_PLAN as unknown as BuildingPlan;
const MANSION_B_HOUSE = MANSION_B_HOUSE_PLAN as unknown as BuildingPlan;
const MANSION_B_UPPER = MANSION_B_UPPER_PLAN as unknown as BuildingPlan;
const MANSION_GARAGE = MANSION_GARAGE_PLAN as unknown as BuildingPlan;

interface MansionStair {
    /** the flight (x0, y0, x1, y1), up towards the north: its top at y0 */
    readonly box: readonly number[]
    /** its rail's line (x) along its side open to the hall */
    readonly rail: number
    /** the closet under the gallery past its top */
    readonly store: readonly number[]
}

const planExtra = <T>(plan: BuildingPlan, key: string): T => plan.extra?.[key] as T;
const boxOf = ([x0, y0, x1, y1]: readonly number[]): RectangleHitbox => planBox(x0, y0, x1, y1);
const middleOf = ([x0, y0, x1, y1]: readonly number[]): Vector => Vec((x0 + x1) / 2, (y0 + y1) / 2);

/** a house's flights (on the stairs' layer), the closets under the gallery and the rails along their open sides */
const mansionStairs = (plan: BuildingPlan, rail: "mansion_stair_rail" | "mansion_glass_rail"): BuildingObstacle[] =>
    planExtra<readonly MansionStair[]>(plan, "stairs").flatMap(({ box, rail: railX, store }) => [
        { idString: "mansion_flight", position: middleOf(box), rotation: 1, layer: Layer.ToUpstairs },
        { idString: "mansion_stair_store", position: middleOf(store), rotation: 0 },
        { idString: rail, position: Vec(railX, (box[1] + box[3]) / 2), rotation: 0 }
    ]);

/** the flights' floors, on the stairs' layer (a floor's layer is the layer itself) */
const mansionStairFloors = (plan: BuildingPlan): NonNullable<BuildingDefinition["floors"]> =>
    planExtra<readonly MansionStair[]>(plan, "stairs").map(({ box }) => ({ type: FloorNames.Wood, hitbox: boxOf(box), layer: Layer.ToUpstairs }));

/** what a house's wardrobes and nightstands hold (not the apartment blocks'): all of them upstairs */
const MANSION_TABLES = { apt_wardrobe: "mansion_wardrobe", apt_nightstand: "small_drawer" } as const;

// ---- A, the old house ----
const MANSION_A_GROUND: readonly BuildingObstacle[] = [
    // ---- the hall: the table under the flowers, the armour either side, palms by the doors
    at("hall_table", 0, 18),
    at("museum_armour_knight", -16.3, 30), at("museum_armour_knight", 16.3, 30, 0),
    at("potted_plant", -16.4, 41.6), at("potted_plant", 16.4, 41.6),
    // ---- the library: the bookcases along its east wall, the fire on its west wall, the chesterfield facing it and a
    // wingback either side of the hearth, the globe, the drinks
    ...[-38.6, -26, -13.4].map(y => at("bookshelf", -23.12, y, 3)),
    at("fireplace", -58.85, -24, 1),
    at("chesterfield", -37.5, -24, 3),
    at("wingback", -50, -36, 0), at("wingback", -50, -12, 2),
    at("globe", -30, -40.5),
    at("drinks_trolley", -29.5, -9, 0),
    at("potted_plant", -60.5, -42.5),
    // ---- the drawing room: the fire on its north wall, two sofas facing each other over the table, the piano, an
    // armchair
    at("fireplace", -36, 1.155, 0),
    at("couch", -47.5, 18, 0), at("couch", -27.5, 18, 2),
    at("apt_coffee_table", -37.5, 18, 1),
    at("apt_armchair", -37.5, 31, 2),
    at("piano", -52, 37, 0),
    at("potted_plant", -60.5, 3), at("potted_plant", -23.5, 42.5),
    // ---- the dining room: the long table laid for twelve, the sideboard, the china cabinet
    at("grand_dining_table", 42, 20, 1),
    ...[7.2, 13.6, 20, 26.4, 32.8].flatMap(y => [at("dining_chair", 34.8, y, 1), at("dining_chair", 49.2, y, 3)]),
    at("dining_chair", 42, 1.3, 0), at("dining_chair", 42, 38.7, 2),
    at("sideboard", 60.8, 24, 3),
    at("china_cabinet", 23, 32, 1),
    at("potted_plant", 60.5, 42.5), at("potted_plant", 23.5, 42.5),
    // ---- the kitchen: the sink and the cupboards under the windows, the range, the island, the fridge
    at("mansion_sink_counter", 33, -42.7, 0), at("mansion_counter", 51, -42.7, 0),
    at("range_cooker", 23.5, -24, 1),
    at("kitchen_island_marble", 44, -26, 0),
    at("fridge", 59.6, -26.5, 3),
    at("trash_can", 60.5, -41.5)
];

const MANSION_A_UPSTAIRS: readonly BuildingObstacle[] = [
    // ---- the gallery: the railing over the hall, a bench under the window, plants
    at("mansion_gallery_rail_a", 0, -29, 0),
    at("museum_bench", 0, -43.5, 0),
    at("potted_plant", -8, -42.5), at("potted_plant", 8, -42.5),
    // ---- the dressing room: wardrobes down both sides
    at("apt_wardrobe", -23.8, -24, 3), at("apt_wardrobe", -23.8, -11.5, 3),
    at("apt_wardrobe", -38.2, -38, 1), at("apt_wardrobe", -38.2, -12.5, 1),
    // ---- the master's bathroom: the double basin, the clawfoot bath, the WC
    at("double_vanity", -53, -42.5, 0),
    at("clawfoot_tub", -56, -24, 1),
    at("toilet", -46, -8),
    // ---- the master's bedroom: the four-poster, its nightstands, the chaise at its foot, the fire, the dressing table
    at("four_poster_bed", -53.5, 23, 1),
    at("apt_nightstand", -61, 12.6, 1), at("apt_nightstand", -61, 33.4, 1),
    at("chaise_longue", -37, 23, 0),
    at("fireplace", -48, 1.155, 0),
    at("vanity_table", -23.3, 30, 3),
    at("potted_plant", -23.5, 42.5),
    // ---- the study: the partners' desk facing the door, the wingbacks before it, the bookcases, the safe, the guns
    at("executive_desk", 44, -24, 1), at("office_chair", 51, -24, 3),
    at("wingback", 33, -29, 1), at("wingback", 33, -19, 1),
    at("bookshelf", 60.88, -38.6, 3), at("bookshelf", 60.88, -12, 3),
    at("mansion_safe", 60.5, -25, 3),
    at("gun_case", 50, -7.4, 2),
    at("globe", 38, -41),
    // ---- the guest room: the bed, its nightstands, the wardrobe, an armchair; its bathroom
    at("bed", 50, 5, 0),
    at("apt_nightstand", 41.8, -0.9, 0), at("apt_nightstand", 58.2, -0.9, 0),
    at("apt_wardrobe", 23.8, 30, 1),
    at("apt_armchair", 30, 8, 0),
    at("bathtub", 54, 40.35, 0), at("toilet", 60, 26), at("sink2", 52, 24.4, 0)
];

// ---- B, the new house ----
const MANSION_B_GROUND: readonly BuildingObstacle[] = [
    // ---- the hall: a bench, a bust, palms
    at("museum_bench", 14.5, 6, 3),
    at("museum_bust", 12.5, -42),
    at("potted_plant", 13.5, 41.5), at("potted_plant", -13.5, 41.5),
    // ---- the great room's kitchen: the counters along the north wall, the fridge, the island and its stools
    at("modern_sink_counter", -47, -42.7, 0), at("modern_counter", -29, -42.7, 0),
    at("fridge", -59.6, -38, 1),
    at("kitchen_island_quartz", -40, -26, 0),
    ...[-47, -40, -33].map(x => at("apt_stool", x, -18.6)),
    // ---- its dining table, its chairs
    at("dining_table_modern", -40, -4, 0),
    ...[-46, -40, -34].flatMap(x => [at("modern_chair", x, -11.1, 0), at("modern_chair", x, 3.1, 2)]),
    at("modern_chair", -54.6, -4, 1), at("modern_chair", -25.4, -4, 3),
    // ---- its lounge: the corner sofa facing the fire in the wall and the television, the marble table
    at("sectional_sofa", -30, 24, 3),
    at("coffee_table_modern", -38, 28, 1),
    at("linear_fireplace", -61.7, 24, 1),
    at("media_console", -46, 43.2, 2),
    at("potted_plant", -60.5, 8),
    // ---- the cinema: three rows of recliners facing the screen, the popcorn
    ...[30, 40, 50].map(x => at("cinema_recliners", x, -23, 1)),
    at("cinema_screen", 62.4, -23, 1),
    at("popcorn_machine", 21, -42),
    // ---- the gym: the weights, the bench, the bag, the treadmills
    at("prison_dumbbell_rack", 28, 3.4, 0),
    at("prison_bench_press", 34, 14, 0),
    at("prison_punching_bag", 26, 33),
    at("prison_weight_tree", 38, 33),
    at("treadmill", 46, 36.5, 2), at("treadmill", 53, 36.5, 2)
];

const MANSION_B_UPSTAIRS: readonly BuildingObstacle[] = [
    // ---- the gallery: its glass railing over the hall, a bench, a plant
    at("mansion_gallery_rail_b", 5, -29, 0),
    at("museum_bench", 5, -43.5, 0),
    at("potted_plant", 13.5, -42.5),
    // ---- the master's suite: its hall (a bench, a plant), the bathroom (the bath standing free, the basins, the WC,
    // the shower drawn on its floor), the dressing room (wardrobes, the safe), the bedroom
    at("museum_bench", -29, -43.5, 0), at("potted_plant", -37.5, -42.5),
    at("freestanding_tub", -54, -12, 0),
    at("floating_vanity", -44.3, -12, 3),
    at("toilet", -60.5, -2.5),
    at("apt_wardrobe", -37.2, -15.5, 1), at("apt_wardrobe", -20.8, -15.5, 3),
    at("mansion_safe", -37.5, -2),
    at("platform_bed", -53, 24.5, 1),
    at("apt_nightstand", -61, 13.4, 1), at("apt_nightstand", -61, 35.6, 1),
    at("dressing_island", -37, 24.5, 1),
    at("apt_armchair", -24, 40, 2),
    at("potted_plant", -20.5, 9),
    // ---- the games room: the billiards table, the bar and its stools, the arcade cabinets, a sofa
    at("billiards_table", 40, -24, 0),
    at("bar_counter", 60.5, -20, 3),
    ...[-26, -20, -14].map(y => at("apt_stool", 55.2, y)),
    at("arcade_cabinet", 60.8, -41.5, 3), at("arcade_cabinet", 60.8, -36.4, 3),
    at("apt_sofa", 28, -5.3, 2),
    at("potted_plant", 21.5, -12),
    // ---- the office: the glass desk facing the door, the bookcase, the safe, two armchairs
    at("glass_desk", 45, 24, 0), at("office_chair", 45, 30.5, 2),
    at("bookshelf", 60.88, 23, 3),
    at("mansion_safe", 60.5, 42.5),
    at("apt_armchair", 25, 12, 1), at("apt_armchair", 25, 22, 1),
    at("potted_plant", 21, 42.5)
];

/** how the doors that would swing into something open (the gap's middle; the point they swing away from) */
const MANSION_A_SWINGS: readonly Swing[] = [
    [-20, 14.075, -40, 14], [20, 14.075, 40, 14],           // the hall's into the hall
    [-52.925, -4, -53, -20],                                // the library's into the drawing room
    [41.925, -4, 42, 10],                                   // the dining room's into the kitchen
    [-64.5, 25.075, -40, 25]                                // the drawing room's garden door out
];
const MANSION_A_UPPER_SWINGS: readonly Swing[] = [
    [-42, -24.925, -52, -25],                               // the bathroom's into the dressing room
    [31.075, -4, 31, 10],                                   // the guest room's into the study
    [44, 33.075, 54, 32]                                    // the guest bathroom's into the guest room
];
const MANSION_B_SWINGS: readonly Swing[] = [
    [17, -21.925, 30, -22],                                 // the cinema's into the hall
    [-64.5, -2.925, -50, -3],                               // the great room's garden door out
    [64.5, 29.075, 40, 29]                                  // the gym's door out
];

/** a house's ground floor (its upper floor on it, at its place, a floor up) */
const mansionHouse = (
    idString: string,
    plan: BuildingPlan,
    floorImage: string,
    upper: string,
    rail: "mansion_stair_rail" | "mansion_glass_rail",
    furniture: readonly BuildingObstacle[],
    swings: readonly Swing[],
    flipped: ReadonlyArray<readonly [number, number]>,
    loot: readonly Vector[]
): BuildingDefinition => ({
    idString,
    name: "Mansion",
    defType: DefinitionType.Building,
    material: "stone",
    particle: "mansion_stone_particle",
    particleVariations: 2,
    collideWithLayers: Layers.Adjacent,
    hasSecondFloor: true,
    hitbox: planWalls(plan),
    spawnHitbox: planSpawn(plan),
    ceilingHitbox: planCeiling(plan, 1.5),
    floorOpenings: boxOf(planExtra<readonly number[]>(plan, "void")),
    floorImages: [{ key: floorImage, position: Vec(0, 0) }],
    ceilingImages: [{ key: "mansion_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
    floors: [...planFloors(plan), ...mansionStairFloors(plan)],
    obstacles: IS_CLIENT ? undefined : [...planOpenings(plan, flipped, swings), ...mansionStairs(plan, rail), ...furniture],
    lootSpawners: IS_CLIENT ? undefined : loot.map(position => ({ table: "ground_loot", position })),
    subBuildings: IS_CLIENT ? undefined : [{ idString: upper, position: Vec(0, 0), layer: Layer.Upstairs }]
});

/** a house's upper floor: its rooms round the gallery, open over the hall */
const mansionUpper = (
    idString: string,
    plan: BuildingPlan,
    floorImage: string,
    furniture: readonly BuildingObstacle[],
    swings: readonly Swing[],
    loot: readonly Vector[]
): BuildingDefinition => ({
    idString,
    name: "Mansion",
    defType: DefinitionType.Building,
    material: "stone",
    particle: "mansion_stone_particle",
    particleVariations: 2,
    collideWithLayers: Layers.Adjacent,
    hasSecondFloor: true,
    hitbox: planWalls(plan),
    spawnHitbox: planSpawn(plan),
    ceilingHitbox: planCeiling(plan, 1.5),
    floorOpenings: boxOf(planExtra<readonly number[]>(plan, "void")),
    floorImages: [{ key: floorImage, position: Vec(0, 0) }],
    ceilingImages: [{ key: "mansion_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
    // (1.08, roofs.ts: the lantern over the hall, down through it to the hall's floor)
    glass: MANSION_LANTERN_GLASS,
    floors: planFloors(plan),
    obstacleLootTables: MANSION_TABLES,
    obstacles: IS_CLIENT ? undefined : [...planOpenings(plan, [], swings, true), ...furniture],
    lootSpawners: IS_CLIENT ? undefined : loot.map(position => ({ table: "ground_loot", position }))
});

/** the grounds' fence: its piers (the drive's gate's bigger), its railings, its gates */
const mansionFence = (): BuildingObstacle[] => {
    const piers = planExtra<ReadonlyArray<readonly number[]>>(MANSION_LOT, "piers")
        .map(([x, y, kind]) => ({ idString: kind === 1 ? "mansion_gate_pier" : "mansion_pier", position: Vec(x, y), rotation: 0 }));
    const rails = planExtra<ReadonlyArray<readonly number[]>>(MANSION_LOT, "rails")
        .map(([x, y, axis]) => ({ idString: "mansion_railing", position: Vec(x, y), rotation: axis }));
    const gates = planExtra<ReadonlyArray<{ readonly x: number, readonly y: number, readonly width: number, readonly axis: string }>>(MANSION_LOT, "gates")
        .flatMap(({ x, y, width, axis }): BuildingObstacle[] => {
            const along = axis === "y" ? y : x;
            const opening: PlanOpening = { kind: "gate", axis, at: axis === "y" ? x : y, from: along - width / 2, to: along + width / 2, t: 1 };
            return width > 15 ? gapDoors("iron_gate", opening, width / 2) : [gapDoor("iron_gate", opening, 0)];
        });
    return [...piers, ...rails, ...gates];
};

const MANSION_FOUNTAIN = planExtra<readonly number[]>(MANSION_LOT, "fountain");
const MANSION_CIRCLE = planExtra<readonly number[]>(MANSION_LOT, "circle");
const MANSION_HOUSE_AT = planExtra<readonly number[]>(MANSION_LOT, "house");
const MANSION_PORTICO = planExtra<readonly number[]>(MANSION_LOT, "portico");
const MANSION_GARAGE_BOX = planExtra<readonly number[]>(MANSION_LOT, "garage");

/** the grounds (both mansions alike), its house the one given */
const mansionGrounds = (idString: string, house: string): BuildingDefinition => ({
    idString,
    name: "Mansion",
    defType: DefinitionType.Building,
    spawnHitbox: planSpawn(MANSION_LOT),
    spawnMode: MapObjectSpawnMode.GrassAndSand,
    floorImages: [{ key: "mansion_grounds", position: Vec(0, 0), scale: Vec(2, 2) }],
    // (the fountain's lawn first, then the drive's gravel round it: where floors overlap, the first wins)
    floors: [
        { type: FloorNames.Grass, hitbox: new CircleHitbox(MANSION_CIRCLE[0], Vec(MANSION_FOUNTAIN[0], MANSION_FOUNTAIN[1])) },
        { type: FloorNames.Stone, hitbox: new CircleHitbox(MANSION_CIRCLE[1], Vec(MANSION_FOUNTAIN[0], MANSION_FOUNTAIN[1])) },
        { type: FloorNames.Stone, hitbox: new GroupHitbox(...planExtra<ReadonlyArray<readonly number[]>>(MANSION_LOT, "gravel").map(boxOf)) },
        { type: FloorNames.Stone, hitbox: new GroupHitbox(...planExtra<ReadonlyArray<readonly number[]>>(MANSION_LOT, "paving").map(boxOf)) },
        { type: FloorNames.Grass, hitbox: planSpawn(MANSION_LOT) }
    ],
    obstacles: IS_CLIENT ? undefined : [
        ...mansionFence(),
        // the fountain in the drive's circle, the lamps along the drive and by the portico
        at("mansion_fountain", MANSION_FOUNTAIN[0], MANSION_FOUNTAIN[1]),
        ...[[40, 49.5], [70, 49.5], [40, 74.5], [70, 74.5], [-21, 36], [21, 36]].map(([x, y]) => at("museum_lamp_post", x, y)),
        // the cars in their bays, nosed out to the drive
        at("mansion_car", 44, 95, 1), at("mansion_car", 60, 95, 1),
        // urns on the terrace and before the portico, benches on the terrace
        ...[[-48, -82], [48, -82], [-13, -88], [13, -88], [-20.5, 31], [20.5, 31]].map(([x, y]) => at("garden_urn", x, y)),
        at("museum_bench", -30, -84, 2), at("museum_bench", 30, -84, 2),
        // the oaks in the garden behind
        at("oak_tree", -64, -94), at("oak_tree", 64, -94),
        // the service side: the air conditioners, the bins
        at("ac_unit", 82.5, -50, 3), at("ac_unit", 82.5, -43, 3),
        at("trash_can", 83, -32), at("trash_can", 83, -26)
    ],
    subBuildings: IS_CLIENT ? undefined : [
        { idString: house, position: Vec(MANSION_HOUSE_AT[0], MANSION_HOUSE_AT[1]) },
        { idString: "fargo_mansion_portico", position: middleOf(MANSION_PORTICO) },
        { idString: "fargo_mansion_garage", position: middleOf(MANSION_GARAGE_BOX) }
    ]
});

export const FARGO_BUILDINGS: readonly BuildingDefinition[] = [
    {
        // the compound: its lots, the plaza, the motor pool and its fence, the station and the sally port in it
        idString: "police_station",
        name: "Police Station",
        defType: DefinitionType.Building,
        spawnHitbox: RectangleHitbox.fromRect(606, 328),
        spawnMode: MapObjectSpawnMode.GrassAndSand,
        graphicsZIndex: ZIndexes.BuildingsFloor - 0.2,
        graphics: [
            { color: 0x4b4b4b, hitbox: POLICE_ASPHALT },
            { color: 0x9a9993, hitbox: POLICE_CONCRETE },
            { color: 0xe8e8e8, hitbox: new GroupHitbox(...policeBayLines()) },
            // the curbs' yellow by the station's doors, the planted strips in the public lot
            { color: 0xd8b23a, hitbox: new GroupHitbox(planBox(-158, -164, -157.2, 164), planBox(-152, -118.8, 100, -118)) },
            { color: 0x5c8444, hitbox: new GroupHitbox(planBox(-297, -2, -205, 2), planBox(-140, 120, -110, 134), planBox(10, 120, 60, 134)) }
        ],
        floors: [
            { type: FloorNames.Stone, hitbox: POLICE_ASPHALT },
            { type: FloorNames.Stone, hitbox: POLICE_CONCRETE }
        ],
        obstacles: IS_CLIENT ? undefined : [
            // ---- the public lot: patrol cars and visitors' cars in its bays, lamp posts, its sign by the main road
            carAt("police_car", -288.7, -77, 1), carAt("parked_car", -255.3, -77, 1), carAt("police_car", -205.3, -43, 3),
            carAt("parked_car", -272, -43, 3), carAt("parked_car", -238.7, 43, 1), carAt("police_car", -288.7, 77, 3),
            carAt("parked_car", -222, 77, 3), carAt("police_car", -255.3, 43, 1),
            ...[[-297, -60], [-197, -60], [-297, 60], [-197, 60], [-170, -140], [-170, 140]].map(([x, y]) => ({
                idString: "museum_lamp_post", position: Vec(x, y)
            })),
            { idString: "bush", position: Vec(-280, 0) }, { idString: "bush", position: Vec(-250, 0) }, { idString: "bush", position: Vec(-220, 0) },
            // ---- the staff lot north of the station: patrol cars nose in to the wall
            carAt("police_car", -138, -134, 3), carAt("police_car", -118, -134, 3), carAt("police_car", -58, -134, 3),
            carAt("parked_car", -38, -134, 3), carAt("police_car", 22, -134, 3), carAt("police_car", 62, -134, 3),
            // ---- the plaza: the flag, the station's sign, planters and benches, bollards before the doors
            { idString: "police_flagpole", position: Vec(-104, 140) },
            { idString: "police_sign", position: Vec(10, 150), rotation: 0 },
            { idString: "museum_bench", position: Vec(-125, 127), rotation: 0 }, { idString: "museum_bench", position: Vec(35, 127), rotation: 0 },
            { idString: "bush", position: Vec(-136, 127) }, { idString: "bush", position: Vec(-114, 127) },
            { idString: "bush", position: Vec(22, 127) }, { idString: "bush", position: Vec(48, 127) },
            ...[-82, -76, -42, -36].map(x => ({ idString: "street_bollard", position: Vec(x, 124) })),
            { idString: "museum_lamp_post", position: Vec(-148, 158) }, { idString: "museum_lamp_post", position: Vec(96, 158) },
            // ---- the motor pool: patrol cars along its fences, the fuel pumps, cones, barriers by the gate
            ...[176, 196, 216, 236, 256].map(x => carAt("police_car", x, -140, 1)),
            ...[176, 196, 236, 276].map(x => carAt("police_car", x, 140, 3)),
            { idString: "police_fuel_pump", position: Vec(220, -8), rotation: 1 },
            { idString: "police_fuel_pump", position: Vec(220, 8), rotation: 1 },
            ...[[180, -40], [186, -40], [192, -40], [262, 36], [268, 40]].map(([x, y]) => ({ idString: "traffic_cone", position: Vec(x, y) })),
            { idString: "police_barrier", position: Vec(258, -60), rotation: 0 }, { idString: "police_barrier", position: Vec(270, -64), rotation: 0 },
            // (the gate onto the road: two barrier arms down across its two lanes, each from its post, its weight by it)
            { idString: "police_gate_arm", position: Vec(299, -10), rotation: 3 }, { idString: "police_gate_arm", position: Vec(299, 10), rotation: 1 },
            { idString: "regular_crate", position: Vec(286, -140) }, { idString: "regular_crate", position: Vec(286, -128) },
            { idString: "barrel", position: Vec(287, 128) },
            ...policeFence(Vec(100, -160), Vec(299, -160)),
            ...policeFence(Vec(100, 160), Vec(299, 160)),
            ...policeFence(Vec(299, -160), Vec(299, -18)),
            ...policeFence(Vec(299, 18), Vec(299, 160))
        ],
        subBuildings: IS_CLIENT ? undefined : [
            { idString: "police_station_hall", position: POLICE_STATION_AT },
            { idString: "police_sally_port", position: POLICE_GARAGE_AT },
            // the canopy over the front doors (their middle 33.14 west of the station's), the motor pool's over its pumps
            { idString: "police_canopy", position: Vec(POLICE_STATION_AT.x - 33.14, 118) },
            { idString: "police_fuel_canopy", position: Vec(220, 0) }
        ]
    },
    {
        idString: "police_canopy",
        name: "Entrance Canopy",
        defType: DefinitionType.Building,
        spawnHitbox: RectangleHitbox.fromRect(26, 12),
        ceilingHitbox: RectangleHitbox.fromRect(26, 12),
        noCeilingScopeEffect: true,
        ceilingImages: [{ key: "police_canopy", position: Vec(0, 0) }],
        obstacles: IS_CLIENT ? undefined : [at("museum_post", -12, 5), at("museum_post", 12, 5)]
    },
    {
        idString: "police_fuel_canopy",
        name: "Fuel Canopy",
        defType: DefinitionType.Building,
        spawnHitbox: RectangleHitbox.fromRect(26, 34),
        ceilingHitbox: RectangleHitbox.fromRect(26, 34),
        noCeilingScopeEffect: true,
        ceilingImages: [{ key: "police_fuel_canopy", position: Vec(0, 0) }],
        obstacles: IS_CLIENT ? undefined : [at("museum_post", -10, 0), at("museum_post", 10, 0)]
    },
    {
        idString: "police_station_hall",
        name: "Police Station",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "police_brick_particle",
        particleVariations: 2,
        hitbox: planWalls(POLICE_PLAN),
        spawnHitbox: planBox(-127, -113, 127, 113),
        // (to the middle of the outer walls: the inside shows to someone outside only through a window or a door)
        ceilingHitbox: planBox(-124.5, -110.5, 124.5, 110.5),
        floorImages: [
            { key: "police_floor_nw", position: Vec(-63, -56) },
            { key: "police_floor_ne", position: Vec(63, -56) },
            { key: "police_floor_sw", position: Vec(-63, 56) },
            { key: "police_floor_se", position: Vec(63, 56) }
        ],
        // (1.08, roofs.ts: its roof's deck, the two hatches in its roof, its skylights' glass over the bullpen, which
        // gives way under whoever lands or walks on it)
        hasSecondFloor: true,
        ceilingImages: [{ key: "police_roof", position: Vec(0, 0), scale: Vec(2, 2) }, ...POLICE_ROOF_PIECES],
        glass: POLICE_GLASS,
        floors: planFloors(POLICE_PLAN),
        obstacles: IS_CLIENT ? undefined : [...planOpenings(POLICE_PLAN), ...POLICE_FURNITURE],
        subBuildings: IS_CLIENT ? undefined : [POLICE_DECK],
        // (three spots of loose loot; the good loot is in the armory's lockers)
        lootSpawners: IS_CLIENT ? undefined : [
            { table: "ground_loot", position: Vec(16, -8) },
            { table: "ground_loot", position: Vec(44, 60) },
            { table: "ground_loot", position: Vec(-12, 70) }
        ]
    },
    {
        // the sally port: a garage on the station's east side, open at both ends, booking's double doors in its west
        // side (the station's wall); the prisoner van in it
        idString: "police_sally_port",
        name: "Sally Port",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "police_brick_particle",
        particleVariations: 2,
        hitbox: new GroupHitbox(
            planBox(22, -38, 25, 38),
            planBox(-25, -38, -15, -35),
            planBox(15, -38, 25, -35),
            planBox(-25, 35, -15, 38),
            planBox(15, 35, 25, 38)
        ),
        spawnHitbox: planBox(-25, -38, 25, 38),
        ceilingHitbox: planBox(-25, -36, 23.5, 36),
        graphicsZIndex: ZIndexes.BuildingsFloor - 0.1,
        graphics: [
            { color: 0x8a8b86, hitbox: planBox(-25, -38, 25, 38) },
            { color: 0xd8b23a, hitbox: new GroupHitbox(planBox(-20, -30, -19.4, 30), planBox(19.4, -30, 20, 30)) }
        ],
        ceilingImages: [{ key: "police_garage_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
        floors: [{ type: FloorNames.Stone, hitbox: planBox(-25, -38, 25, 38) }],
        obstacles: IS_CLIENT ? undefined : [
            carAt("police_van", 2, 0, 1),
            { idString: "traffic_cone", position: Vec(-18, -30) },
            { idString: "traffic_cone", position: Vec(-18, 30) }
        ]
    },
    {
        // the prison's lighthouse: the tower, its passage and the watch post; outside, the searchlight on the prison
        idString: "prison_lighthouse",
        name: "Prison Lighthouse",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        spawnMode: MapObjectSpawnMode.GrassAndSand,
        hitbox: new GroupHitbox(...planWalls(LIGHTHOUSE_PLAN).hitboxes, ...LIGHTHOUSE_RING),
        spawnHitbox: planSpawn(LIGHTHOUSE_PLAN),
        ceilingHitbox: new GroupHitbox(
            new CircleHitbox(LIGHTHOUSE_TOWER.radius - LIGHTHOUSE_TOWER.wall / 2, LIGHTHOUSE_AT),
            planRoom(LIGHTHOUSE_PLAN, "passage"),
            planBox(-20.5, -12.5, 20.5, 32.5)
        ),
        floorImages: [{ key: "prison_lighthouse_floor", position: Vec(0, -11) }],
        // (1.08, roofs.ts: the watch post's roof's deck, the ladder's top over its east wall; the tower's glided off)
        hasSecondFloor: true,
        ceilingImages: [{ key: "prison_lighthouse_roof", position: Vec(0, -11) }, ...LIGHTHOUSE_ROOF_PIECES],
        floors: [
            { type: FloorNames.Stone, hitbox: new CircleHitbox(LIGHTHOUSE_TOWER.radius, LIGHTHOUSE_AT) },
            { type: FloorNames.Stone, hitbox: planRoom(LIGHTHOUSE_PLAN, "passage") },
            { type: FloorNames.Wood, hitbox: planRoom(LIGHTHOUSE_PLAN, "bunks") },
            { type: FloorNames.Stone, hitbox: planRoom(LIGHTHOUSE_PLAN, "watch") }
        ],
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(LIGHTHOUSE_PLAN),
            // the tower: its spiral stair up to the lantern (closed off), the keeper's old crate
            at("lighthouse_stair", LIGHTHOUSE_AT.x, LIGHTHOUSE_AT.y),
            { idString: "lighthouse_crate", position: Vec(7.5, -48) },
            at("barrel", -8, -49),
            // the bunk room: the guards' bunks, their gun locker, the strong crate, a stove and a table
            at("bunk_bed", -14.3, -2.8, 0),
            at("gun_locker", 12, -8.9, 0),
            { idString: "aegis_crate", position: Vec(-13.8, 2.2) },
            at("small_stove", 15.8, 3.6, 3),
            at("small_table", 6, 2, 1),
            // the watch room: the desk under the windows on the prison, its chair, files, water
            at("watch_desk", -8, 28.7, 2), at("office_chair", -8, 23.2, 0),
            at("filing_cabinet", 16, 12.4, 0),
            at("water_cooler", 16, 27.4, 2),
            at("small_drawer", -15.6, 12.4, 0),
            // outside: the searchlight behind its sandbags, turned on the prison; fuel for the generator
            at("searchlight", 0, 50, 0, { outdoors: true }),
            at("sandbags", -11, 58.5, 0, { outdoors: true }), at("sandbags", 11, 58.5, 0, { outdoors: true }),
            at("sandbags", -21, 47, 1, { outdoors: true }), at("sandbags", 21, 47, 1, { outdoors: true }),
            at("barrel", 27, 30, 0, { outdoors: true }), at("propane_tank", 27, 22, 0, { outdoors: true })
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(4, 18) }],
        subBuildings: IS_CLIENT ? undefined : [LIGHTHOUSE_DECK]
    },
    {
        // Pool Day (1.07.1): the compound's own ground, the deck round the pool and the hot tub, open to the sky; its four
        // buildings stand round its edge
        idString: "fargo_pool",
        name: "Pool Day",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        spawnMode: MapObjectSpawnMode.GrassAndSand,
        spawnHitbox: planSpawn(POOL_COMPOUND),
        floorImages: [{ key: "fargo_pool_deck", position: Vec(0, 0), scale: Vec(2, 2) }],
        // (the pool's and the hot tub's water first: where floors overlap, the first wins)
        floors: [
            { type: FloorNames.Water, hitbox: planBox(POOL_WATER[0], POOL_WATER[1], POOL_WATER[2], POOL_WATER[3]) },
            { type: FloorNames.Water, hitbox: new CircleHitbox(POOL_TUB[2], Vec(POOL_TUB[0], POOL_TUB[1])) },
            { type: FloorNames.Stone, hitbox: planRoom(POOL_COMPOUND, "deck") }
        ],
        obstacles: IS_CLIENT ? undefined : [
            // the pool: the springboard over the deep end, the ladders, the inflatables floating in it
            at("pool_diving_board", POOL_WATER[0] - 2, 0, 0),
            at("pool_ladder", -46, POOL_WATER[1] + 0.6, 2), at("pool_ladder", -46, POOL_WATER[3] - 0.6, 0),
            at("pool_ladder", 28, POOL_WATER[1] + 0.6, 2), at("pool_ladder", 28, POOL_WATER[3] - 0.6, 0),
            at("pool_float", -20, 6), at("swim_ring", 8, -7), at("swim_ring", -36, 9), at("beach_ball", 22, 5),
            // the sunbeds along both sides, the lifeguard's chair between the north ones, the clock over them
            ...poolSunbeds(-31, 0), ...poolSunbeds(31, 2),
            at("lifeguard_chair", -6, -23, 0), at("pool_clock", -6, -36),
            // beach balls left on the deck, its showers, a wet floor sign, the bins at its ends (a player's way left
            // clear of every arch)
            at("beach_ball", -72, 22), at("beach_ball", 62, 37),
            at("pool_shower_post", -86, 0), at("pool_shower_post", 56, 2),
            at("wet_floor_sign", 54, -12),
            at("trash_can", -74, -14), at("trash_can", -74, 14), at("trash_can", 80, -12), at("trash_can", 80, 14)
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(70, 24) }],
        subBuildings: IS_CLIENT ? undefined : [
            { idString: "fargo_pool_blue", position: poolPart("blue") },
            { idString: "fargo_pool_red", position: poolPart("red") },
            { idString: "fargo_pool_showers", position: poolPart("showers") },
            { idString: "fargo_pool_lobby", position: poolPart("lobby") }
        ]
    },
    {
        // the blue hall (the men's): its toilets, its showers round their middle wall, the lockers between its glass
        // entrances, the benches; its colonnade on the deck
        idString: "fargo_pool_blue",
        name: "Changing Room",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        hitbox: planWalls(POOL_BLUE),
        spawnHitbox: planSpawn(POOL_BLUE),
        ceilingHitbox: planCeiling(POOL_BLUE, 1.2),
        floorImages: [
            { key: "fargo_pool_blue_floor_w", position: Vec(-POOL_BLUE.half[0] / 2, 0) },
            { key: "fargo_pool_blue_floor_e", position: Vec(POOL_BLUE.half[0] / 2, 0) }
        ],
        ceilingImages: [{ key: "fargo_pool_blue_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
        // (1.08, roofs.ts: the skylights along its ridge)
        glass: POOL_HALL_GLASS,
        floors: planFloors(POOL_BLUE),
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(POOL_BLUE),
            // the toilets: one in each cubicle, the urinals on their wall, the basins under the mirror, a bin between
            // them and the door (clear of its way)
            ...POOL_BLUE_STALLS.map(x => at("toilet", x, -18.4, 0)),
            at("pool_urinal", -76.7, -6, 3), at("pool_urinal", -76.7, -1, 3),
            at("pool_vanity", -104, 3.4, 2),
            at("trash_can", -93, 2.6),
            // the showers' bench along their west wall
            at("pool_bench", 77.6, -12, 1),
            // the locker room: the lockers along the north wall and its end walls, the benches, the vending machine by
            // the way to the shower side, the bins by the end walls (clear of the colonnade), a wet floor sign
            ...poolLockers("pool_lockers_blue", -20.9, 0, -12),
            at("pool_bench", -24, -4, 0), at("pool_bench", 0, -4, 0), at("pool_bench", 24, -4, 0),
            at("pool_bench", -48, 9, 0), at("pool_bench", 48, 9, 0),
            at("vending_machine", -106, 10.1, 0),
            at("trash_can", -71.5, 1), at("trash_can", 71.5, 1),
            at("wet_floor_sign", 72, 14)
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(0, 12) }, { table: "ground_loot", position: Vec(-36, -12) }]
    },
    {
        // the red hall (the women's): its showers in their stalls, its toilets, the vanity island with its mirrors in
        // the middle, the lockers between its glass entrances; its colonnade on the deck
        idString: "fargo_pool_red",
        name: "Changing Room",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        hitbox: planWalls(POOL_RED),
        spawnHitbox: planSpawn(POOL_RED),
        ceilingHitbox: planCeiling(POOL_RED, 1.2),
        floorImages: [
            { key: "fargo_pool_red_floor_w", position: Vec(-POOL_RED.half[0] / 2, 0) },
            { key: "fargo_pool_red_floor_e", position: Vec(POOL_RED.half[0] / 2, 0) }
        ],
        ceilingImages: [{ key: "fargo_pool_red_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
        glass: POOL_HALL_GLASS,
        floors: planFloors(POOL_RED),
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(POOL_RED),
            // the showers: a bench before their stalls
            at("pool_bench", -96, 6, 0),
            // the toilets: one in each cubicle, the basins under the mirror, a bin
            ...POOL_RED_STALLS.map(x => at("toilet", x, 18.4, 2)),
            at("pool_vanity", 107, -3.4, 0),
            at("trash_can", 80, -2),
            // the changing room: the vanity island, the lockers along the south wall and its end walls, the benches,
            // the vending machine in the corner by the toilets, the bins by the end walls (clear of the colonnade)
            at("pool_vanity_island", POOL_ISLAND[0], POOL_ISLAND[1], 0),
            ...poolLockers("pool_lockers_red", 20.9, 2, 12),
            at("pool_bench", -40, -8, 0), at("pool_bench", 40, -8, 0), at("pool_bench", -40, 9, 0), at("pool_bench", 40, 9, 0),
            at("vending_machine", 106, -10.1, 2),
            at("trash_can", -71.5, -1), at("trash_can", 71.5, -1)
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(0, -12) }, { table: "ground_loot", position: Vec(36, 12) }]
    },
    {
        // the shower side: the long wall down its middle with the showers on both its faces, the door to the beach,
        // its colonnade on the deck
        idString: "fargo_pool_showers",
        name: "Showers",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        hitbox: planWalls(POOL_SHOWERS),
        spawnHitbox: planSpawn(POOL_SHOWERS),
        ceilingHitbox: planCeiling(POOL_SHOWERS, 1.2),
        floorImages: [{ key: "fargo_pool_showers_floor", position: Vec(0, 0) }],
        // (1.08, roofs.ts: its roof's deck, the hatch by the beach door, its four skylights' glass)
        hasSecondFloor: true,
        ceilingImages: [{ key: "fargo_pool_showers_roof", position: Vec(0, 0), scale: Vec(2, 2) }, ...POOL_SHOWERS_ROOF_PIECES],
        glass: POOL_SHOWERS_GLASS,
        floors: planFloors(POOL_SHOWERS),
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(POOL_SHOWERS),
            at("wet_floor_sign", 6, -38)
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(6, 36) }],
        subBuildings: IS_CLIENT ? undefined : [POOL_SHOWERS_DECK]
    },
    {
        // the lobby: the glass doors in, the ticket desk facing them; the lifeguard's office looking out on the pool,
        // the snack kiosk serving the deck through its hatch
        idString: "fargo_pool_lobby",
        name: "Lobby",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "rock_particle",
        particleVariations: 2,
        hitbox: planWalls(POOL_LOBBY),
        spawnHitbox: planSpawn(POOL_LOBBY),
        ceilingHitbox: planCeiling(POOL_LOBBY, 1.2),
        floorImages: [{ key: "fargo_pool_lobby_floor", position: Vec(0, 0) }],
        // (1.08, roofs.ts: its roof's deck, the hatch over the hall by the arches onto the deck)
        hasSecondFloor: true,
        ceilingImages: [{ key: "fargo_pool_lobby_roof", position: Vec(0, 0), scale: Vec(2, 2) }, ...POOL_LOBBY_ROOF_PIECES],
        floors: planFloors(POOL_LOBBY),
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(POOL_LOBBY),
            // the lifeguard's office: the desk under the window on the pool, the chair, the first aid cabinet on the
            // blue hall's wall, the stretcher
            at("watch_desk", -9.3, -33, 1), at("office_chair", -4.4, -33, 3),
            at("first_aid_cabinet", 4, -43.2, 0),
            at("pool_stretcher", 9.1, -35, 0),
            // the hall: the ticket desk in its middle facing the doors, the clerk's chair behind it (a way left past it
            // to the deck's arches, and to the office's and the kiosk's doors: 1.07.1's lab found a bench in the kiosk's
            // and the chair in an arch), plants by those doors
            at("pool_ticket_desk", 1, 0, 1), at("office_chair", -3.9, 0, 1),
            at("potted_plant", -2, -18.6), at("potted_plant", -2, 18.6),
            // the kiosk: its counter along the hatch, the freezer, the popcorn machine, its menu, the stock's crate
            at("kiosk_counter", -9.6, 33, 3),
            at("ice_cream_freezer", 9.4, 33, 3),
            at("popcorn_machine", 9.9, 40),
            at("kiosk_menu", -5.5, 23.2, 0),
            at("regular_crate", 1, 38)
        ],
        lootSpawners: IS_CLIENT ? undefined : [{ table: "ground_loot", position: Vec(7, 14) }],
        subBuildings: IS_CLIENT ? undefined : [POOL_LOBBY_DECK]
    },
    // ---------------- the mansions (MANSION_LOT and the notes above it) ----------------
    mansionGrounds("fargo_mansion_a", "fargo_mansion_a_house"),
    mansionGrounds("fargo_mansion_b", "fargo_mansion_b_house"),
    mansionHouse("fargo_mansion_a_house", MANSION_A_HOUSE, "mansion_a_ground", "fargo_mansion_a_upper", "mansion_stair_rail",
        MANSION_A_GROUND, MANSION_A_SWINGS, [], [Vec(-46, -24), Vec(-30, 40)]),
    mansionUpper("fargo_mansion_a_upper", MANSION_A_UPPER, "mansion_a_upper", MANSION_A_UPSTAIRS, MANSION_A_UPPER_SWINGS, [Vec(-30, 12)]),
    // (its back door hinged at its east end, clear of the closet)
    mansionHouse("fargo_mansion_b_house", MANSION_B_HOUSE, "mansion_b_ground", "fargo_mansion_b_upper", "mansion_glass_rail",
        MANSION_B_GROUND, MANSION_B_SWINGS, [[0, -46.5]], [Vec(-36, 21), Vec(4, -14)]),
    mansionUpper("fargo_mansion_b_upper", MANSION_B_UPPER, "mansion_b_upper", MANSION_B_UPSTAIRS, [], [Vec(40, -12)]),
    {
        // the portico before the front doors: its roof on four columns over the steps
        idString: "fargo_mansion_portico",
        name: "Portico",
        defType: DefinitionType.Building,
        spawnHitbox: RectangleHitbox.fromRect(MANSION_PORTICO[2] - MANSION_PORTICO[0], MANSION_PORTICO[3] - MANSION_PORTICO[1]),
        ceilingHitbox: RectangleHitbox.fromRect(MANSION_PORTICO[2] - MANSION_PORTICO[0], MANSION_PORTICO[3] - MANSION_PORTICO[1]),
        noCeilingScopeEffect: true,
        ceilingImages: [{ key: "mansion_portico_roof", position: Vec(0, 0) }],
        obstacles: IS_CLIENT ? undefined : [-14.5, -6, 6, 14.5].map(x => at("museum_column", x, 4.2))
    },
    {
        // the garage: two bays, the car in the one shut, the workbench and a crate in the open one; its side door
        idString: "fargo_mansion_garage",
        name: "Garage",
        defType: DefinitionType.Building,
        material: "stone",
        particle: "mansion_stone_particle",
        particleVariations: 2,
        hitbox: planWalls(MANSION_GARAGE),
        spawnHitbox: planSpawn(MANSION_GARAGE),
        ceilingHitbox: planCeiling(MANSION_GARAGE, 1),
        floorImages: [{ key: "mansion_garage_floor", position: Vec(0, 0) }],
        ceilingImages: [{ key: "mansion_garage_roof", position: Vec(0, 0), scale: Vec(2, 2) }],
        floors: planFloors(MANSION_GARAGE),
        obstacles: IS_CLIENT ? undefined : [
            ...planOpenings(MANSION_GARAGE),
            at("mansion_car", 3, -12, 0),
            at("workbench", 0, 20.3, 2),
            at("regular_crate", -14.5, 10)
        ]
    }
];
